import { useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Button, SpinningLoader, Text, TextField, useAlert, useControlledField } from '@frontend/design-system';
import { getErrorMessage } from './helpers';
import { useReplacementTrains } from './hooks/use-replacement-trains';

const TRAIN_OPTION_HEIGHT = 56;
const LOAD_MORE_THRESHOLD_PX = 120;

interface TrainPickerProps {
  label: string;
  name: string;
  selectedId: string;
  onSelect: (trainId: string) => void;
  disabled?: boolean;
  /** Train that is already assigned; rendered disabled with a "(current)" suffix. */
  currentTrainId?: string;
  /** When provided, renders a button to clear the current selection. */
  onClear?: () => void;
}

/**
 * Searchable, virtualized, infinitely-loading list of trains. Intended to be rendered inside modal content so
 * that its search state resets whenever the modal is closed.
 */
export const TrainPicker = ({
  label,
  name,
  selectedId,
  onSelect,
  disabled,
  currentTrainId,
  onClear,
}: TrainPickerProps) => {
  const alerts = useAlert();
  const {
    trains,
    totalTrains,
    hasMoreTrains,
    isLoadingTrains,
    isLoadingMoreTrains,
    isTrainsError,
    loadMoreTrains,
    searchQuery,
    setSearchQuery,
    searchedTrain,
    isSearching,
    isSearchPending,
    isSearchError,
  } = useReplacementTrains({
    enabled: true,
    onLoadMoreError: (error) => alerts.error(`Failed to load more trains: ${getErrorMessage(error)}`),
  });
  const searchFieldProps = useControlledField({ type: 'text', value: searchQuery, onChange: setSearchQuery });
  const trainListRef = useRef<HTMLDivElement>(null);
  const scrollFrameRef = useRef<number>();
  const displayedTrains = isSearching ? (searchedTrain ? [searchedTrain] : []) : trains;

  const virtualizer = useVirtualizer({
    count: displayedTrains.length,
    getScrollElement: () => trainListRef.current,
    estimateSize: () => TRAIN_OPTION_HEIGHT,
    overscan: 5,
  });

  useEffect(
    () => () => {
      if (scrollFrameRef.current !== undefined) cancelAnimationFrame(scrollFrameRef.current);
    },
    []
  );

  // Throttled to one check per animation frame to avoid running on every scroll event.
  const handleTrainListScroll = () => {
    if (scrollFrameRef.current !== undefined) return;

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = undefined;
      const trainList = trainListRef.current;

      if (
        !trainList ||
        isSearching ||
        !hasMoreTrains ||
        isLoadingMoreTrains ||
        trainList.scrollHeight - trainList.scrollTop - trainList.clientHeight > LOAD_MORE_THRESHOLD_PX
      ) {
        return;
      }

      loadMoreTrains();
    });
  };

  return (
    <>
      <TextField {...searchFieldProps} name={name} label={label} placeholder='Search by train ID' disabled={disabled} />
      <Text size='small' css={searchHintStyle}>
        Searches all trains by exact train ID.
      </Text>
      {onClear && selectedId && (
        <Button variant='tertiary' disabled={disabled} onClick={onClear}>
          Clear train selection
        </Button>
      )}
      {isLoadingTrains || isSearchPending ? (
        <div css={loadingTrainsStyle} role='status'>
          <SpinningLoader size='small' />
          <Text>Loading trains...</Text>
        </div>
      ) : !isSearching && isTrainsError && trains.length === 0 ? (
        <div css={queryErrorStyle} role='alert'>
          <Text color='error'>Unable to load trains.</Text>
        </div>
      ) : isSearching && displayedTrains.length === 0 ? (
        <div css={queryErrorStyle} role={isSearchError ? 'alert' : 'status'}>
          <Text color={isSearchError ? 'error' : 'subdued'}>
            {isSearchError ? 'Unable to load train.' : 'Train not found.'}
          </Text>
        </div>
      ) : (
        <div
          ref={trainListRef}
          role='listbox'
          aria-label='Available trains'
          css={trainListStyle}
          onScroll={handleTrainListScroll}
        >
          <div style={{ height: virtualizer.getTotalSize(), position: 'relative' }}>
            {virtualizer.getVirtualItems().map((virtualItem) => {
              const train = displayedTrains[virtualItem.index];
              const trainId = train?.trainId ?? '';
              const trainName = train?.trainName ?? trainId;
              const isCurrentTrain = currentTrainId !== undefined && trainId === currentTrainId;
              const isSelected = trainId === selectedId;

              return (
                <Button
                  key={trainId}
                  role='option'
                  aria-selected={isSelected}
                  label={trainName}
                  variant={isSelected ? 'secondary' : 'tertiary'}
                  disabled={disabled || isCurrentTrain}
                  css={[trainOptionStyle, isSelected && selectedTrainOptionStyle]}
                  onClick={() => onSelect(trainId)}
                  style={{ transform: `translateY(${virtualItem.start}px)` }}
                >
                  <span css={trainOptionContentStyle}>
                    <span>
                      {trainName}
                      {isCurrentTrain ? ' (current)' : ''}
                    </span>
                    <span css={trainIdStyle}>{trainId}</span>
                  </span>
                </Button>
              );
            })}
          </div>
        </div>
      )}
      {!isSearching && (hasMoreTrains || totalTrains !== undefined) && (
        <div css={footerStyle}>
          {hasMoreTrains && (
            <Button
              label='Load more trains'
              variant='primary'
              loading={isLoadingMoreTrains}
              disabled={disabled}
              onClick={() => loadMoreTrains()}
            >
              Load more trains
            </Button>
          )}
          {totalTrains !== undefined && (
            <Text color='primary' css={trainCountStyle} size='medium'>
              Showing {trains.length.toLocaleString()} of {totalTrains.toLocaleString()} Trains
            </Text>
          )}
        </div>
      )}
    </>
  );
};

const searchHintStyle = css`
  color: var(--fabric-color-text-subtle);
`;

const footerStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const loadingTrainsStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--fabric-space-gap-xs);
  height: 264px;
  border: var(--fabric-border-width-thin) solid var(--fabric-color-border-default);
  border-radius: var(--fabric-radius-sm);
`;

const queryErrorStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
  padding: var(--fabric-space-padding-sm);
`;

const trainListStyle = css`
  height: 264px;
  overflow-y: auto;
  border: var(--fabric-border-width-thin) solid var(--fabric-color-border-default);
  border-radius: var(--fabric-radius-sm);
`;

const trainOptionStyle = css`
  position: absolute;
  left: 0;
  width: 100%;
  height: var(--fabric-number-n-14-px);
  padding: 0 var(--fabric-space-padding-md);
  justify-content: flex-start;
  border-radius: 0;
`;

const selectedTrainOptionStyle = css`
  background-color: var(--fabric-color-surface-selected-default);
  box-shadow: inset var(--fabric-border-width-thick) 0 0 var(--fabric-color-border-interactive);
`;

const trainOptionContentStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--fabric-number-n-0-5-px);
  overflow: hidden;
  text-align: left;
`;

const trainIdStyle = css`
  overflow: hidden;
  max-width: 100%;
  color: var(--fabric-color-text-subtle);
  font-size: var(--fabric-font-size-xs);
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const trainCountStyle = css`
  text-align: center;
`;
